import { Injectable } from '@angular/core';
import { Firestore, collection, collectionData, query, where, orderBy, doc, deleteDoc, getDoc, getDocs, addDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../_services/auth.service';
import { Result, AdminSession } from '../_models/result';
import { parseSessionIdInput } from './parse-session-id';

interface CalcApiAdminRequest {
  schoolId: string;
  sessionId: string;
  adminUsePublishedRules: boolean;
}

@Injectable()
export class InspectorService {
  sessions: Observable<AdminSession[]> = of([]);
  loadedSchoolId = '';
  token = '';

  constructor(
    private firestore: Firestore,
    private auth: AuthService,
    private functions: Functions,
  ) { }

  public async loadSessions(schoolId: string, all: boolean) {
    if (schoolId !== this.loadedSchoolId) {
      if (all) {
        this.loadAll(schoolId);
      } else {
        const user = await this.auth.currentUser();
        const sessionsCollection = collection(this.firestore, 'sessions_admin');
        const q = query(sessionsCollection, where('userId', '==', user?.uid || ''), where('univName', '==', schoolId));
        this.sessions = collectionData(q) as Observable<AdminSession[]>;
      }
      this.loadedSchoolId = schoolId;
    }
  }

  public loadAll(schoolId: string) {
    const sessionCol = collection(this.firestore, 'sessions_admin');
    const q = query(sessionCol, where('univName', '==', schoolId), orderBy('savedAt', 'desc'));
    this.sessions = (collectionData(q, { idField: 'id' }) as Observable<AdminSession[]>).pipe(
      map(sessions => {
        return sessions.map(session => {
          const sessionName = this.resolveSessionName(session);
          return { ...session, sessionName };
        });
      }));
  }

  public deleteAdminSession(sessionId: string) {
    const docRef = doc(this.firestore, 'sessions_admin', sessionId);
    return deleteDoc(docRef);
  }

  public cloudCalc(schoolId: string, sessionId: string, publishedRules: boolean): Observable<Result | null> {
    const callable = httpsCallable<CalcApiAdminRequest, Result>(this.functions, 'calcApiAdmin');
    return from(callable({ schoolId, sessionId, adminUsePublishedRules: publishedRules })).pipe(
      map(res => res.data),
      catchError(() => {
        return of(null);
      })
    );
  }

  public async createAdminSession(sessionIdOrUrl: string): Promise<string> {
    const parsed = parseSessionIdInput(sessionIdOrUrl);
    if ('error' in parsed) {
      return parsed.error;
    }

    const session = await this.findStudentSession(parsed.sessionId);

    if (!session) {
      return 'No session found';
    }

    if (session.univName !== this.loadedSchoolId) {
      return 'This session is for a different school';
    }

    const suggestedName = this.resolveSessionName(session);

    const sessionsCollection = collection(this.firestore, 'sessions_admin');
    const q = query(sessionsCollection, where('savedAt', '==', session.savedAt), where('univName', '==', session.univName));
    const existingSession = await getDocs(q);

    if (!existingSession.empty) {
      const existingName =
        existingSession.docs[0].data()['sessionName']
        || suggestedName
        || 'Untitled session';
      return 'A session from this date already exists with the name: ' + existingName;
    }

    const enteredName = prompt('Name this session', suggestedName);
    if (enteredName === null) {
      return 'Session creation cancelled';
    }
    const sessionName = enteredName.trim() || suggestedName || 'Untitled session';

    const user = await this.auth.currentUser();

    const adminSession = {
      answers: session.answers,
      cbUserId: session.cbUserId,
      savedAt: session.savedAt,
      univName: session.univName,
      userId: user?.uid || '',
      sessionName,
      // Newer sessions use answers[].hidden instead of preparedAnswers.
      // Omit the legacy field when absent so Firestore does not receive `undefined`.
      ...(session.preparedAnswers !== undefined ? { preparedAnswers: session.preparedAnswers } : {})
    };
    await addDoc(collection(this.firestore, 'sessions_admin'), adminSession);
    return 'Success.  Created session with the name: ' + sessionName;
  }

  // Student sessions are split by CB identity (userSessions / guestSessions);
  // fall back to legacy sessions while older docs still exist.
  private async findStudentSession(sessionId: string): Promise<AdminSession | null> {
    const collections = ['userSessions', 'guestSessions', 'sessions'] as const;
    for (const collectionName of collections) {
      try {
        const snap = await getDoc(doc(this.firestore, collectionName, sessionId));
        if (snap.exists()) {
          return snap.data() as AdminSession;
        }
      } catch {
        // Missing docs can fail resource-based read rules; try the next collection.
      }
    }
    return null;
  }

  private resolveSessionName(session: AdminSession): string {
    if (session.sessionName) {
      return session.sessionName;
    }
    return session.answers.find(a => a.questionId === 'student.firstName')?.value?.toString() ?? '';
  }

}
